'use client';

import { useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { MAPS_URL } from '@/constants/contact';
import { navItems } from '@/constants/nav-list';
import { useAuth } from '@/hooks/use-auth';
import { useLogout } from '@/hooks/use-logout';
import { Button, cn, Sheet } from '@holistic/components';
import {
  ChevronDown,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Phone,
  User,
  UserPlus,
  Wrench,
} from '@holistic/components/icons';

import type { NavCategory } from '@/types/navigation';

/** Kategoriler header'daki mega menü ile aynı `getNavigation()` verisinden gelir. */
const MobileSheet = ({ categories }: { categories: NavCategory[] | null }) => {
  const path = usePathname();
  const [open, setOpen] = useState(false);
  const [tamirOpen, setTamirOpen] = useState(false);
  const { user } = useAuth();
  const logout = useLogout();
  const close = () => setOpen(false);

  // Mega menüdeki gibi: kategori yoksa 'Tamir Talebi' bölümü hiç açılmaz.
  const hasNavigation = !!categories && categories.length > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <Sheet.Trigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Menüyü aç"
          className={cn(
            'lg:hidden',
            path.includes('login') || path.includes('register') ? 'hidden' : ''
          )}
        >
          <Menu className="h-5 w-5" aria-hidden />
        </Button>
      </Sheet.Trigger>

      <Sheet.Content
        side="left"
        className="flex w-72 flex-col p-0"
        aria-describedby={undefined}
      >
        <Sheet.Header className="border-b px-5 py-4">
          <Sheet.Title className="sr-only">Menü</Sheet.Title>
          <Image
            src="/yeniden-siyah-logo.svg"
            alt="Yeniden.co"
            width={1011}
            height={214}
            className="h-5.5 w-auto"
          />
        </Sheet.Header>

        <nav className="flex-1 overflow-y-auto px-3 py-3">
          <ul className="flex flex-col gap-0.5">
            {navItems.map((item) => (
              <li key={item.href}>
                <Sheet.Close asChild>
                  <Link
                    href={item.href}
                    onClick={close}
                    className="flex items-center rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    {item.label}
                  </Link>
                </Sheet.Close>
              </li>
            ))}

            {hasNavigation && (
              <li>
                <button
                  onClick={() => setTamirOpen((p) => !p)}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                >
                  Tamir Talebi
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${tamirOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {tamirOpen && (
                  <ul className="mt-1 ml-3 flex flex-col gap-0.5 border-l border-gray-100 pl-3">
                    {categories.map((cat) => (
                      <li key={cat.categoryId}>
                        <Sheet.Close asChild>
                          <Link
                            href={`/talep?category=${cat.categoryId}`}
                            onClick={close}
                            className="flex items-center rounded-lg px-2 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                          >
                            {cat.categoryName}
                          </Link>
                        </Sheet.Close>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            )}
          </ul>
        </nav>

        <div className="border-t px-3 py-3">
          {user ? (
            <ul className="flex flex-col gap-0.5">
              {[
                { href: '/profilim', label: 'Profilim', Icon: User },
                {
                  href: '/profilim/taleplerim',
                  label: 'Tamir Taleplerim',
                  Icon: Wrench,
                },
              ].map(({ href, label, Icon }) => (
                <li key={href}>
                  <Sheet.Close asChild>
                    <Link
                      href={href}
                      onClick={close}
                      className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <Icon size={15} />
                      {label}
                    </Link>
                  </Sheet.Close>
                </li>
              ))}
              <li>
                <button
                  onClick={() => {
                    logout();
                    close();
                  }}
                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut size={15} />
                  Çıkış Yap
                </button>
              </li>
            </ul>
          ) : (
            <div className="flex flex-col gap-2">
              <Sheet.Close asChild>
                <Link
                  href="/login"
                  onClick={close}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <LogIn size={15} />
                  Giriş Yap
                </Link>
              </Sheet.Close>
              <Sheet.Close asChild>
                <Link
                  href="/register"
                  onClick={close}
                  className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <UserPlus size={15} />
                  Kayıt Ol
                </Link>
              </Sheet.Close>
            </div>
          )}
        </div>

        {/* Hızlı İletişim, WhatsApp ve Yol Tarifi */}
        <div className="border-t bg-gray-50/70 p-3">
          <p className="px-1 pb-1.5 text-[11px] font-semibold tracking-wider text-gray-500 uppercase">
            Hızlı İletişim
          </p>
          <div className="grid grid-cols-2 gap-1.5 text-xs">
            <a
              href="tel:+908503338444"
              className="hover:border-primary hover:text-primary flex items-center gap-1.5 rounded-lg border border-gray-200/80 bg-white p-2 font-medium text-gray-700 transition-colors"
              aria-label="Telefon ile ara: 0850 333 8 444"
            >
              <Phone className="text-primary size-3.5 shrink-0" />
              <span className="truncate">0850 333 8 444</span>
            </a>
            <a
              href="https://wa.me/908503338444"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 rounded-lg border border-emerald-200/80 bg-emerald-50/70 p-2 font-medium text-emerald-800 transition-colors hover:bg-emerald-100"
              aria-label="WhatsApp Destek Hattı"
            >
              <span className="size-2 shrink-0 rounded-full bg-emerald-500" />
              <span className="truncate font-semibold">WhatsApp</span>
            </a>
            <a
              href="mailto:info@yeniden.co"
              className="hover:border-primary hover:text-primary flex items-center gap-1.5 rounded-lg border border-gray-200/80 bg-white p-2 font-medium text-gray-700 transition-colors"
              aria-label="E-posta gönder: info@yeniden.co"
            >
              <Mail className="text-primary size-3.5 shrink-0" />
              <span className="truncate">info@yeniden.co</span>
            </a>
            <a
              href={MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:border-primary hover:text-primary flex items-center gap-1.5 rounded-lg border border-gray-200/80 bg-white p-2 font-medium text-gray-700 transition-colors"
              aria-label="Haritada Yol Tarifi Al"
            >
              <MapPin className="text-primary size-3.5 shrink-0" />
              <span className="truncate">Yol Tarifi</span>
            </a>
          </div>
        </div>
      </Sheet.Content>
    </Sheet>
  );
};

export default MobileSheet;
